/**
 * The in-memory people index (PLAN §3.6, S2). One row per person, merged by
 * user id from every source:
 *   dm      DM partners (the DM is the row's target — never two rows)
 *   member  members of every channel and group in chatStore
 *   team    team members, loaded once per session in idle time (10k cap)
 *   server  name-search hits from GET /chat/users/search (§12.3.1)
 *
 * Built lazily, never on a keystroke unless the chat list changed:
 *   - each channel's members are read once per channel OBJECT (zustand keeps
 *     unchanged channels' identity), so a new message in one channel re-reads
 *     only that one;
 *   - when no channel object changed, nothing is rebuilt at all;
 *   - a person whose shown fields did not change keeps the same Result object,
 *     and an unchanged list keeps its identity (the scorer caches per list).
 */
import { refOf } from '../types';

export const TEAM_MEMBER_CAP = 10000;
const SRC = { dm: 1, member: 2, team: 4, server: 8 };

function tsOf(channel) {
  const raw = channel?.last_message?.inserted_at || channel?.updated_at || channel?.inserted_at;
  const t = raw ? Date.parse(raw) : NaN;
  return Number.isFinite(t) ? t : 0;
}

function personFromMember(m) {
  const u = m?.user || m || {};
  const id = m?.user_id ?? u.id ?? m?.id;
  if (id == null) return null;
  return { userId: String(id), name: u.name || m?.name || '', email: u.email || m?.email || '' };
}

export function createPeopleIndex({ getChannels, getCurrentUserId, t = (k) => k } = {}) {
  let lastChannels = [];
  let builtFrom = null;
  let builtUser = null;
  let chatPeople = new Map();
  const perChannel = new WeakMap(); // channel object -> members read once
  const extra = new Map(); // userId -> person (team / server), survives chat rebuilds
  let results = null;
  let list = null;
  let dirty = true;
  const sigs = new Map(); // userId -> what the row showed, to reuse unchanged rows

  function membersOf(ch, me) {
    let m = perChannel.get(ch);
    if (m && m.me === me) return m;
    const members = ch?.members || [];
    if (ch?.kind === 'dm') {
      const other = members.find((x) => String(x.user_id) !== me);
      m = { me, dm: other ? personFromMember(other) : null, people: null, ts: tsOf(ch) };
    } else {
      const people = [];
      for (const x of members) {
        const p = personFromMember(x);
        if (p && p.userId !== me) people.push(p);
      }
      m = { me, dm: null, people, ts: 0 };
    }
    if (ch && typeof ch === 'object') perChannel.set(ch, m);
    return m;
  }

  function rebuildChat() {
    const channels = getChannels() || [];
    const me = getCurrentUserId() != null ? String(getCurrentUserId()) : null;
    if (channels === builtFrom && me === builtUser) return false;
    // A new array with the same channel objects (a re-sort, an unrelated field): nothing to do.
    const sameObjects = me === builtUser && channels.length === lastChannels.length && channels.every((c, i) => c === lastChannels[i]);
    builtFrom = channels;
    builtUser = me;
    lastChannels = channels.slice();
    if (sameObjects) return false;
    const map = new Map();
    for (const ch of channels) {
      if (!ch) continue;
      const m = membersOf(ch, me);
      if (m.dm) {
        const p = m.dm;
        const prev = map.get(p.userId);
        if (prev) {
          prev.src |= SRC.dm;
          prev.dmChannelId = ch.id;
          prev.lastActivity = Math.max(prev.lastActivity, m.ts);
          if (p.name) prev.name = p.name;
          if (p.email) prev.email = p.email;
        } else {
          map.set(p.userId, { userId: p.userId, name: p.name, email: p.email, dmChannelId: ch.id, lastActivity: m.ts, channelName: null, teamName: null, src: SRC.dm });
        }
        continue;
      }
      for (const p of m.people || []) {
        const prev = map.get(p.userId);
        if (prev) {
          prev.src |= SRC.member;
          if (!prev.name && p.name) prev.name = p.name;
          if (!prev.email && p.email) prev.email = p.email;
          if (!prev.channelName && ch.name) prev.channelName = ch.name;
        } else {
          map.set(p.userId, { userId: p.userId, name: p.name, email: p.email, dmChannelId: null, lastActivity: 0, channelName: ch.name || null, teamName: null, src: SRC.member });
        }
      }
    }
    chatPeople = map;
    return true;
  }

  function toResult(p) {
    const email = p.email || '';
    const local = email.split('@')[0];
    return {
      provider: 'people',
      id: p.userId,
      title: p.name || email || t('palette.fallback.person'),
      subtitle: email || (p.dmChannelId ? t('palette.people.directMessage') : p.teamName || p.channelName || undefined),
      badge: p.dmChannelId ? t('palette.people.dm') : p.teamName || undefined,
      avatarUserId: p.userId,
      ts: p.lastActivity || undefined,
      searchFields: local && local !== email ? [{ text: local.replace(/[._-]+/g, ' '), weight: 0.5 }] : [],
      payload: { userId: p.userId, name: p.name, email: email || null, channelId: p.dmChannelId || null },
    };
  }

  function ensure() {
    if (!(rebuildChat() || dirty || !results)) return results;
    dirty = false;
    const prev = results;
    const next = new Map();
    let same = !!prev;
    const take = (p) => {
      const sig = `${p.name}|${p.email}|${p.dmChannelId || ''}|${p.lastActivity || 0}|${p.teamName || ''}|${p.channelName || ''}`;
      const old = prev && prev.get(p.userId);
      if (old && sigs.get(p.userId) === sig) next.set(p.userId, old);
      else {
        next.set(p.userId, toResult(p));
        sigs.set(p.userId, sig);
        same = false;
      }
    };
    // Chat data wins (it carries the DM); team / server data fills the gaps.
    for (const p of chatPeople.values()) {
      const e = extra.get(p.userId);
      take(e ? { ...p, name: p.name || e.name, email: p.email || e.email, teamName: e.teamName } : p);
    }
    for (const e of extra.values()) if (!chatPeople.has(e.userId)) take(e);
    if (!prev || prev.size !== next.size) same = false;
    results = next;
    if (!same) list = null;
    return results;
  }

  /** Add people from another source (team roster, server hits); returns the ones that were new. */
  function addPeople(people, source, { teamName = null } = {}) {
    const fresh = [];
    for (const raw of people || []) {
      const p = personFromMember(raw);
      if (!p || p.userId === String(getCurrentUserId())) continue;
      if (extra.size >= TEAM_MEMBER_CAP && !extra.has(p.userId)) break;
      const known = extra.get(p.userId);
      if (known) {
        known.src |= SRC[source] || 0;
        if (!known.email && p.email) known.email = p.email;
        if (!known.teamName && teamName) known.teamName = teamName;
        continue;
      }
      extra.set(p.userId, { ...p, dmChannelId: null, lastActivity: 0, channelName: null, teamName, src: SRC[source] || 0 });
      if (!chatPeople.has(p.userId)) fresh.push(p.userId);
    }
    if ((people || []).length) dirty = true;
    return fresh;
  }

  return {
    ensure,
    /** The same array until the data changes (the scorer caches per array). */
    all: () => {
      const map = ensure();
      if (!list) list = [...map.values()];
      return list;
    },
    get: (id) => ensure().get(String(id)) || null,
    has: (id) => ensure().has(String(id)),
    addPeople,
    ref: (id) => refOf('people', id),
    get size() {
      return ensure().size;
    },
    reset() {
      builtFrom = null;
      lastChannels = [];
      chatPeople = new Map();
      extra.clear();
      results = null;
      list = null;
      sigs.clear();
      dirty = true;
    },
  };
}
